// Usage page (ADR-118 Phase 5D, #1258): wired to the real ListUsageEvents
// RPC. account_id is REQUIRED server-side (Console's RLS context is scoped
// per-account per transaction), so the page always starts with an account
// selector fed by ListAccounts -- there is no "all accounts" view, matching
// the RPC's own invariant rather than working around it client-side.
//
// Honest gap (documented per task brief step 3): license_id,
// reporting_source, and deployment_type read back empty/UNSPECIFIED on
// every event today -- confirmed by reading services/console's
// ListUsageEvents implementation and ingestion path, this is a
// server-side population gap, not a UI bug. The table therefore does not
// render those columns; a note below the table says so explicitly instead
// of showing misleading blank cells.
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens + glass-card chrome; data layer untouched.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as pb from '@/generated/console_pb'
import { listAccounts, listUsageEvents } from '@/lib/grpcweb/console-api'
import { dataClassificationLabel, formatTimestamp } from '@/lib/formatters'
import { DARK, GREY_300, GREY_400, GREY_500, WHITE, errorBlockStyle } from '@/lib/genesis'

const PAGE_SIZE = 50

export function UsagePage() {
  const [accountId, setAccountId] = useState('')
  const [instanceId, setInstanceId] = useState('')
  const [eventType, setEventType] = useState('')
  const [pageToken, setPageToken] = useState('')
  const [pageStack, setPageStack] = useState<string[]>([]) // tokens for "back"

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts-for-usage'],
    queryFn: () => listAccounts({}),
  })

  const {
    data: usageData,
    isLoading: usageLoading,
    isError: usageError,
  } = useQuery({
    queryKey: ['usage-events', accountId, instanceId, eventType, pageToken],
    queryFn: () =>
      listUsageEvents({
        accountId,
        instanceId: instanceId || undefined,
        eventType: eventType || undefined,
        pageSize: PAGE_SIZE,
        pageToken: pageToken || undefined,
      }),
    enabled: accountId.length > 0,
  })

  const events = usageData?.getEventsList() ?? []

  const summary = useMemo(() => {
    const counts = new Map<string, number>()
    for (const ev of events) {
      const key = ev.getEventType() || '(unset)'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1])
  }, [events])

  function goNext() {
    const next = usageData?.getNextPageToken()
    if (!next) return
    setPageStack((s) => [...s, pageToken])
    setPageToken(next)
  }

  function goPrev() {
    setPageStack((s) => {
      if (s.length === 0) return s
      const copy = [...s]
      const prev = copy.pop() as string
      setPageToken(prev)
      return copy
    })
  }

  function resetPaging() {
    setPageToken('')
    setPageStack([])
  }

  return (
    <div>
      <h1 className="font-display mb-4 text-xl font-semibold" style={{ color: WHITE }}>Usage</h1>

      <div className="glass-card mb-4 flex flex-wrap items-end gap-3 p-4">
        <div>
          <label htmlFor="usage-account" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Account
          </label>
          <select
            id="usage-account"
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value)
              resetPaging()
            }}
            className="glass-input w-56 px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          >
            <option value="">
              {accountsLoading ? 'Loading accounts…' : 'Select an account…'}
            </option>
            {accountsData?.getAccountsList().map((acct) => (
              <option key={acct.getId()} value={acct.getId()}>
                {acct.getName()}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="usage-instance" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Instance ID (optional)
          </label>
          <input
            id="usage-instance"
            value={instanceId}
            onChange={(e) => {
              setInstanceId(e.target.value)
              resetPaging()
            }}
            placeholder="filter by instance_id"
            className="glass-input w-56 px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          />
        </div>
        <div>
          <label htmlFor="usage-event-type" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
            Event type (optional)
          </label>
          <input
            id="usage-event-type"
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value)
              resetPaging()
            }}
            placeholder="filter by event_type"
            className="glass-input w-56 px-2 py-1.5 text-sm"
            style={{ color: WHITE }}
          />
        </div>
      </div>

      {!accountId && (
        <p className="text-sm" style={{ color: GREY_400 }}>
          Select an account to load its usage events. ListUsageEvents requires an
          account_id — there is no cross-account view.
        </p>
      )}

      {accountId && usageLoading && (
        <p className="text-sm" style={{ color: GREY_400 }}>Loading usage events…</p>
      )}
      {accountId && usageError && (
        <div className="px-3 py-2" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load usage events.</p>
        </div>
      )}

      {accountId && !usageLoading && !usageError && (
        <>
          {summary.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {summary.map(([type, count]) => (
                <span
                  key={type}
                  className="rounded-full px-2 py-1 text-xs"
                  style={{ backgroundColor: DARK, color: GREY_300 }}
                >
                  {type}: <span className="font-mono" style={{ color: WHITE }}>{count}</span>
                </span>
              ))}
            </div>
          )}

          {events.length === 0 && (
            <p className="text-sm" style={{ color: GREY_400 }}>No usage events for this page of filters.</p>
          )}

          {events.length > 0 && (
            <div className="glass-card overflow-x-auto">
              <table className="w-full min-w-[720px] border-collapse text-sm">
                <thead>
                  <tr className="text-left" style={{ borderBottom: `1px solid ${GREY_500}`, color: GREY_400 }}>
                    <th className="px-3 py-2 font-medium">Occurred at</th>
                    <th className="px-3 py-2 font-medium">Event type</th>
                    <th className="px-3 py-2 font-medium">Instance</th>
                    <th className="px-3 py-2 font-medium">Org ID</th>
                    <th className="px-3 py-2 font-medium">Active seats</th>
                    <th className="px-3 py-2 font-medium">Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev: pb.UsageEvent) => (
                    <tr key={ev.getId()} className="transition-colors hover:bg-white/5" style={{ borderBottom: `1px solid ${DARK}` }}>
                      <td className="px-3 py-2 font-mono" style={{ color: GREY_300 }}>
                        {formatTimestamp(ev.getOccurredAt())}
                      </td>
                      <td className="px-3 py-2" style={{ color: WHITE }}>{ev.getEventType() || '—'}</td>
                      <td className="px-3 py-2" style={{ color: GREY_400 }}>{ev.getInstanceId() || '—'}</td>
                      <td className="px-3 py-2" style={{ color: GREY_400 }}>{ev.getOrgId() || '—'}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: GREY_400 }}>
                        {ev.getActiveSeatCount() || '—'}
                      </td>
                      <td className="px-3 py-2" style={{ color: GREY_400 }}>
                        {dataClassificationLabel(ev.getDataClassification())}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={pageStack.length === 0}
              onClick={goPrev}
              className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
              style={{ color: GREY_300 }}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={!usageData?.getNextPageToken()}
              onClick={goNext}
              className="glass-button rounded-md px-2.5 py-1 text-xs disabled:opacity-40"
              style={{ color: GREY_300 }}
            >
              Next
            </button>
          </div>

          <p className="mt-4 max-w-2xl text-xs" style={{ color: GREY_500 }}>
            Note: <code className="rounded px-1 py-0.5 font-mono" style={{ backgroundColor: DARK }}>license_id</code>,{' '}
            <code className="rounded px-1 py-0.5 font-mono" style={{ backgroundColor: DARK }}>reporting_source</code>, and{' '}
            <code className="rounded px-1 py-0.5 font-mono" style={{ backgroundColor: DARK }}>deployment_type</code> read back
            empty/unspecified on every event as of this build — a documented server-side gap in
            <code className="rounded px-1 py-0.5 font-mono" style={{ backgroundColor: DARK }}>services/console</code>'s
            ingestion/population path, not a UI omission. Those columns are intentionally not
            rendered above; the summary counts and columns shown are all populated fields.
          </p>
        </>
      )}
    </div>
  )
}
